/*
** $Id$
**
** Copyright 2018 Port25 Solutions, Inc.
** All rights reserved.
*/


import React, { Component } from 'react';
import { Container, Row, Col, FormGroup, FormLabel, FormControl, Form, Button,
         FormCheck } from 'react-bootstrap';
import '../css/button.css';
import '../css/viewoptions.css';
import PropTypes from 'prop-types';


/**
 * Provides options to filter and sort results showed in a page.
 */
export default class ViewOptions extends Component {
    constructor(props) {
        super(props)
        let options = {}
        options[this.props.name] = this.props.defaultName
        if (this.props.showVmta) {
            options['vmta'] = '*'
        }
        if (this.props.showPaused) {
            options['paused'] = 'both'
        }
        if (this.props.showMode) {
            options['mode'] = 'any'
        }
        this.state = Object.assign(options, {
            sort: 'rcpt',
            connected: 'both',
            maxItems: '10'
        })
        this.updateName = this.updateName.bind(this)
        this.updateVmta = this.updateVmta.bind(this)
        this.updateSort = this.updateSort.bind(this)
        this.updateConnections = this.updateConnections.bind(this)
        this.updatePaused = this.updatePaused.bind(this)
        this.updateMode = this.updateMode.bind(this)
        this.updateMaxItems = this.updateMaxItems.bind(this)
        this.update = this.update.bind(this)
    }


    static propTypes = {
        name: PropTypes.string.isRequired,
        defaultName: PropTypes.string.isRequired,
        showPaused: PropTypes.bool.isRequired,
        showMode: PropTypes.bool.isRequired,
        showVmta: PropTypes.bool.isRequired,
        onUpdate: PropTypes.func.isRequired
    };


    /**
     * Updates the name filter.
     * 
     * @param event
     *      the event triggered by changing the input text.
     */
    updateName(event) {
        let option = {}
        option[this.props.name] = event.target.value
        this.setState(option)
    }


    /**
     * Updates the vmta filter.
     * 
     * @param event
     *      the event triggered by changing the input text.
     */
    updateVmta(event) {
        this.setState({
            vmta: event.target.value
        })
    }


    /**
     * Updates the sort by filter.
     * 
     * @param event
     *      the event triggered by changing the radio button.
     */
    updateSort(event) {
        this.setState({
            sort: event.target.value
        })
    }


    /**
     * Updates the connections filter.
     * 
     * @param event
     *      the event triggered by changing the radio button.
     */
    updateConnections(event) {
        this.setState({
            connected: event.target.value
        })
    }


    /**
     * Updates the paused filter.
     * 
     * @param event
     *      the event triggered by changing the radio button.
     */
    updatePaused(event) {
        this.setState({
            paused: event.target.value
        })
    }


    /**
     * Updates the mode filter.
     * 
     * @param event
     *      the event triggered by changing the radio button.
     */
    updateMode(event) {
        this.setState({
            mode: event.target.value
        })
    }


    /**
     * Updates the max items filter.
     * 
     * @param event
     *      the event triggered by changing the input text.
     */
    updateMaxItems(event) {
        this.setState({
            maxItems: event.target.value
        })
    }


    /**
     * Updates the page.
     * 
     * @param event
     *      the event triggered by submitting the form.
     */
    update(event) {
        event.preventDefault()
        this.props.onUpdate(this.state, true)
    }


    /**
     * Makes a radio button with the given options.
     *
     * @param name
     *      name of the radio button, must be same for those belonging to a
     *      single group (i.e. if you want them to be exclusive).
     * @param label
     *      label displayed to the user.
     * @param value
     *      value of the radio button.
     * @param onChange
     *      how to handle a change.
     * @param isDefault
     *      whether this radio button is checked by default.
     * @return
     *      the newly created radio button.
     */
    makeRadio(name, label, value, onChange, isDefault) {
        return (
            <FormCheck name={name} value={value}
                   onChange={onChange}
                   defaultChecked={isDefault}
                   label={label}
                   type='radio'/>
        )
    }


    /**
     * Makes radio buttons for sort options.
     *
     * @return
     *      radio buttons for sort options.
     */
    makeSortOptions() {
        let name = "Sort by"
        return <FormGroup>
            <FormLabel>
                {name}
            </FormLabel>
            {this.makeRadio(name, "name", "name", this.updateSort)}
            {this.makeRadio(name, "# of recipients", "rcpt", this.updateSort,
                            true)}
            {this.makeRadio(name, "queue size", "size", this.updateSort)}
        </FormGroup>
    }


    /**
     * Makes radio buttons for connection options.
     *
     * @return
     *      radio buttons for connection options.
     */
    makeConnectionOptions() {
        let name = "Connections"
        return <FormGroup>
            <FormLabel>
                {name}
            </FormLabel>
            {this.makeRadio(name, "connected only", "yes",
                            this.updateConnections)}
            {this.makeRadio(name, "unconnected only","no",
                            this.updateConnections)}
            {this.makeRadio(name, "both", "both",
                            this.updateConnections, true)}
        </FormGroup>
    }


    /**
     * Makes radio buttons for paused options.
     *
     * @return
     *      radio buttons for paused options.
     */
    makePausedOptions() {
        let name = "Paused"
        return <FormGroup>
            <FormLabel>
                {name}
            </FormLabel>
            {this.makeRadio(name, "paused only", "yes",
                            this.updatePaused)}
            {this.makeRadio(name, "running only","no",
                            this.updatePaused)}
            {this.makeRadio(name, "both", "both",
                            this.updatePaused, true)}
        </FormGroup>
    }


    /**
     * Makes radio buttons for mode options.
     *
     * @return
     *      radio buttons for mode options.
     */
    makeModeOptions() {
        let name = "Mode"
        return <FormGroup>
            <FormLabel>
                {name}
            </FormLabel>
            {this.makeRadio(name, "normal only", "normal",
                            this.updateMode)}
            {this.makeRadio(name, "backoff only","backoff",
                            this.updateMode)}
            {this.makeRadio(name, "any", "any",
                            this.updateMode, true)}
        </FormGroup>
    }


    formatName() {
        return this.props.name[0].toUpperCase() +
               this.props.name.slice(1) + " name"
    }


    render() {
        // @release 5.0 Should we validate the input fields, e.g. queue name?
        // Is the message from PowerMTA good enough?  Seems like injection
        // attacks are not of concern: See "JSX Prevents Injection Attacks" at
        // https://reactjs.org/docs/introducing-jsx.html
        return (
            <Form onSubmit={this.update}>
                <Container className='optionsContainer'>
                    <Row>
                        <Col md={4}>
                            <FormGroup>
                                <FormLabel>
                                    {this.formatName()}
                                </FormLabel>
                                <FormControl type="text"
                                    placeholder={this.props.defaultName}
                                    value={this.state[this.props.name]}
                                    onChange={this.updateName}/>
                            </FormGroup>
                            <FormGroup>
                                <FormLabel>
                                    Maximum # of items to display
                                </FormLabel>
                                <FormControl type="text"
                                    placeholder="10" 
                                    value={this.state.maxItems}
                                    onChange={this.updateMaxItems}/>
                            </FormGroup>
                        </Col>
                        {
                            this.props.showVmta &&
                            <Col md={4}>
                                <FormGroup>
                                    <FormLabel>
                                        VMTA
                                    </FormLabel>
                                    <FormControl type="text"
                                        placeholder='*'
                                        value={this.state.vmta}
                                        onChange={this.updateVmta}/>
                                </FormGroup>
                            </Col>
                        }
                        <Col md={2}>
                            { this.makeSortOptions() }
                        </Col>
                        <Col md={2}>
                            { this.makeConnectionOptions() }
                        </Col>
                        {
                            this.props.showPaused &&
                            <Col md={2}>
                                { this.makePausedOptions() }
                            </Col>
                        }
                        {
                            this.props.showMode &&
                            <Col md={2}>
                                { this.makeModeOptions() }
                            </Col>
                        }
                    </Row>
                    <Row>
                        <Col md={12}>
                            <FormGroup className="center">
                                <Button bsPrefix='button' type='submit'>
                                    Update
                                </Button>
                            </FormGroup>
                        </Col>
                    </Row>
                </Container>
            </Form>
        );
    }
}
